import type { ColumnChartData } from '@libs/models/charts/columnChart'
import type { PieChartData } from '@libs/models/charts/pieChart'
import { formatCategories, formatEqualTo, formatLegends, formatSubTitle, formatTitle } from '@libs/models/formatters'
import { findChart } from '@/assets/libs/models/charts/charts'
import { ComparisonEnum, type Rotation } from '../interfaces/interfaces'

export const getEqualTo = (rotation: Rotation): string => {
	const throwError = (rotation: Rotation) => {
		console.error('getEqualTo: cannot get equalTo, invalid arguments', {
			isProduction: rotation.isProduction,
			type: rotation.description.equalTo.type,
			value: rotation.description.equalTo.value,
		})

		return ''
	}

	return rotation.description.equalTo.type &&
		!isNaN(rotation.description.equalTo.value) &&
		rotation.isProduction !== undefined
		? formatEqualTo(rotation)
		: throwError(rotation)
}

const getData = (data: Rotation, transformer: Function) => {
	// needs to be formatted for grouping
	const rtn = [transformer(data, 'currentData')]
	if (data.comparison === ComparisonEnum.periodically || data.comparison === ComparisonEnum.accumulated) {
		// Only push reference columns if we actually have reference data
		if (data.chart.comparisonPartialData?.some((value) => value)) rtn.push(transformer(data, 'comparisonPartialData'))
		if (data.chart.comparisonDeltaData?.some((value) => value)) rtn.push(transformer(data, 'comparisonDeltaData'))
	}
	return rtn
}

const createChart = (data: Rotation, locale: string): Highcharts.Options => {
	const chartObj = findChart(data.rotationType, data.energyType)
	if (!chartObj) {
		throw new Error(`No chart found for rotationType: ${data.rotationType}, energyType: ${data.energyType}`)
	}
	const title = formatTitle(data.rotationType, data.energyType, data.consumptionType)
	const subTitle = formatSubTitle(data, locale)
	const chartSeries = chartObj.config?.series
	const series0 = chartSeries?.[0] as any
	const series1 = chartSeries?.[1] as any
	const series2 = chartSeries?.[2] as any
	if (data.rotationType === 'single' && data.comparison === ComparisonEnum.accumulated) {
		if (series0) series0.stacking = 'stacking'
		if (series2) series2.pointPlacement = 0
		// chartObj.config?.series?.[1].dataLabels.enabled = true
		if (series1) series1.opacity = 0.8
		// chartObj.config?.series?.[2].stacking = 'stacking'
	} else if (data.rotationType === 'single') {
		if (series1?.dataLabels) series1.dataLabels.enabled = false
		if (series0) series0.stacking = undefined
		if (series1) series1.opacity = 1
	}

	return chartObj.chart({
		categories: formatCategories(data.chart.categories.labels, data.chart.categories.type, data.resolution),
		comparison: data.comparison,
		config: chartObj.config,
		consumptionType: data.consumptionType,
		data: getData(data, chartObj.dataTransformer),
		legends: formatLegends(data),
		rotateYLabels: data.chart.categories.labels.length > 2,
		showYAxis: data.chart.showYAxis,
		subTitle,
		title,
		unit: data.unit,
	} as ColumnChartData | PieChartData)
}

export default createChart
